import { Injectable, signal, computed, effect } from '@angular/core';

// Theme interfaces
export interface ThemeColors {
  primary: string;
  primaryHover: string;
  primaryFocus: string;
  primaryLight: string;
  secondary: string;
  secondaryHover: string;
  success: string;
  successLight: string;
  warning: string;
  warningLight: string;
  danger: string;
  dangerLight: string;
  info: string;
  infoLight: string;
  gray50: string;
  gray100: string;
  gray200: string;
  gray300: string;
  gray400: string;
  gray500: string;
  gray600: string;
  gray700: string;
  gray800: string;
  gray900: string;
  white: string;
  black: string;
}

export interface Theme {
  name: string;
  displayName: string;
  colors: ThemeColors;
}

// Predefined themes
export const THEMES: Record<string, Theme> = {
  blue: {
    name: 'blue',
    displayName: 'Blue Theme',
    colors: {
      primary: '#007aff',
      primaryHover: '#0056cc',
      primaryFocus: 'rgba(0, 122, 255, 0.2)',
      primaryLight: 'rgba(0, 122, 255, 0.1)',
      secondary: '#6b7280',
      secondaryHover: '#4b5563',
      success: '#10b981',
      successLight: 'rgba(16, 185, 129, 0.1)',
      warning: '#f59e0b',
      warningLight: 'rgba(245, 158, 11, 0.1)',
      danger: '#ef4444',
      dangerLight: 'rgba(239, 68, 68, 0.1)',
      info: '#3b82f6',
      infoLight: 'rgba(59, 130, 246, 0.1)',
      gray50: '#f9fafb',
      gray100: '#f3f4f6',
      gray200: '#e5e7eb',
      gray300: '#d1d5db',
      gray400: '#9ca3af',
      gray500: '#6b7280',
      gray600: '#4b5563',
      gray700: '#374151',
      gray800: '#1f2937',
      gray900: '#111827',
      white: '#ffffff',
      black: '#000000',
    },
  },
  green: {
    name: 'green',
    displayName: 'Green Theme',
    colors: {
      primary: '#10b981',
      primaryHover: '#059669',
      primaryFocus: 'rgba(16, 185, 129, 0.2)',
      primaryLight: 'rgba(16, 185, 129, 0.1)',
      secondary: '#6b7280',
      secondaryHover: '#4b5563',
      success: '#10b981',
      successLight: 'rgba(16, 185, 129, 0.1)',
      warning: '#f59e0b',
      warningLight: 'rgba(245, 158, 11, 0.1)',
      danger: '#ef4444',
      dangerLight: 'rgba(239, 68, 68, 0.1)',
      info: '#3b82f6',
      infoLight: 'rgba(59, 130, 246, 0.1)',
      gray50: '#f9fafb',
      gray100: '#f3f4f6',
      gray200: '#e5e7eb',
      gray300: '#d1d5db',
      gray400: '#9ca3af',
      gray500: '#6b7280',
      gray600: '#4b5563',
      gray700: '#374151',
      gray800: '#1f2937',
      gray900: '#111827',
      white: '#ffffff',
      black: '#000000',
    },
  },
  purple: {
    name: 'purple',
    displayName: 'Purple Theme',
    colors: {
      primary: '#8b5cf6',
      primaryHover: '#7c3aed',
      primaryFocus: 'rgba(139, 92, 246, 0.2)',
      primaryLight: 'rgba(139, 92, 246, 0.1)',
      secondary: '#6b7280',
      secondaryHover: '#4b5563',
      success: '#10b981',
      successLight: 'rgba(16, 185, 129, 0.1)',
      warning: '#f59e0b',
      warningLight: 'rgba(245, 158, 11, 0.1)',
      danger: '#ef4444',
      dangerLight: 'rgba(239, 68, 68, 0.1)',
      info: '#3b82f6',
      infoLight: 'rgba(59, 130, 246, 0.1)',
      gray50: '#f9fafb',
      gray100: '#f3f4f6',
      gray200: '#e5e7eb',
      gray300: '#d1d5db',
      gray400: '#9ca3af',
      gray500: '#6b7280',
      gray600: '#4b5563',
      gray700: '#374151',
      gray800: '#1f2937',
      gray900: '#111827',
      white: '#ffffff',
      black: '#000000',
    },
  },
};

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  // Current theme signal
  private readonly currentThemeSignal = signal<string>('blue');

  // Computed theme object
  currentTheme = computed(() => THEMES[this.currentThemeSignal()]);

  // Computed colors for easy access
  colors = computed(() => this.currentTheme().colors);

  // Available themes
  availableThemes = computed(() => Object.values(THEMES));

  constructor() {
    // Load theme from localStorage
    const savedTheme = localStorage.getItem('app-theme');
    if (savedTheme && THEMES[savedTheme]) {
      this.currentThemeSignal.set(savedTheme);
    }

    // Apply CSS custom properties when theme changes
    effect(() => {
      this.applyCSSCustomProperties();
      this.saveThemeToStorage();
    });
  }

  /**
   * Set the current theme
   */
  setTheme(themeName: string): void {
    if (THEMES[themeName]) {
      this.currentThemeSignal.set(themeName);
    } else {
      console.warn(
        `Theme "${themeName}" not found. Available themes:`,
        Object.keys(THEMES),
      );
    }
  }

  /**
   * Get current theme name
   */
  getCurrentThemeName(): string {
    return this.currentThemeSignal();
  }

  /**
   * Get a specific color from current theme
   */
  getColor(colorKey: keyof ThemeColors): string {
    return this.colors()[colorKey];
  }

  /**
   * Apply CSS custom properties to document root
   */
  private applyCSSCustomProperties(): void {
    const root = document.documentElement;
    const colors = this.colors();

    // Apply all theme colors as CSS custom properties
    Object.entries(colors).forEach(([key, value]) => {
      const cssVarName = `--theme-${this.kebabCase(key)}`;
      root.style.setProperty(cssVarName, value);
    });
  }

  /**
   * Save current theme to localStorage
   */
  private saveThemeToStorage(): void {
    localStorage.setItem('app-theme', this.currentThemeSignal());
  }

  /**
   * Convert camelCase to kebab-case
   */
  private kebabCase(str: string): string {
    return str
      .replaceAll(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2')
      .toLowerCase();
  }

  /**
   * Create CSS custom property name for a color
   */
  getCSSVar(colorKey: keyof ThemeColors): string {
    return `var(--theme-${this.kebabCase(colorKey)})`;
  }

  /**
   * Get RGB values for a color (useful for rgba with opacity)
   */
  getColorRGB(colorKey: keyof ThemeColors): string {
    const color = this.getColor(colorKey);
    // Convert hex to RGB
    const hex = color.replace('#', '');
    const r = Number.parseInt(hex.substring(0, 2), 16);
    const g = Number.parseInt(hex.substring(2, 4), 16);
    const b = Number.parseInt(hex.substring(4, 6), 16);
    return `${r}, ${g}, ${b}`;
  }

  /**
   * Get RGBA color with custom opacity
   */
  getColorRGBA(colorKey: keyof ThemeColors, opacity: number): string {
    const rgb = this.getColorRGB(colorKey);
    return `rgba(${rgb}, ${opacity})`;
  }
}
